import { lazy, Suspense, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/shared/auth/AuthProvider";
import { loadFirstRunState } from "@/shared/onboarding/first-run";
import { useMarketingExperience } from "@/shared/i18n/use-product-locale";
import { publicWebCopyForLang } from "@/shared/i18n/public-web-copy";
import {
  hasAndroidWebPreference,
  isAndroidBrowserAudience,
} from "@/features/marketing/android-install-browser";
import { androidEntryDestination } from "@/features/marketing/android-install-policy.mjs";
import {
  claimLazyRouteReload,
  clearLazyRouteReload,
  isLazyRouteLoadError,
} from "./lazy-route-recovery.mjs";

const AndroidLanding = lazy(async () => {
  let storage: Storage | null = null;
  try {
    storage = window.sessionStorage;
  } catch {
    /* Recovery is optional. */
  }
  try {
    const module = await import("@/features/marketing/LandingPage");
    clearLazyRouteReload(storage, "android-landing");
    return { default: module.LandingPage };
  } catch (error) {
    if (isLazyRouteLoadError(error) && claimLazyRouteReload(storage, "android-landing")) {
      window.location.reload();
      return new Promise<{
        default: typeof import("@/features/marketing/LandingPage").LandingPage;
      }>(() => {});
    }
    throw error;
  }
});

/** New Android visitors see the app; returning readers keep their direct entry. */
export function AppStartPage() {
  const { signedIn, booting } = useAuth();
  // Freeze this visit's acquisition choice: dismissing the dock must not navigate.
  const [audience] = useState(isAndroidBrowserAudience);
  const [suppressed] = useState(hasAndroidWebPreference);
  const { lang } = useMarketingExperience();
  const loading = (
    <div className="route-fallback" role="status" style={{ minHeight: "calc(100svh - 5rem)" }}>
      {publicWebCopyForLang(lang)("auth.route.loading")}
    </div>
  );
  if (booting) return loading;
  const destination = androidEntryDestination({
    signedIn,
    completed: loadFirstRunState().completed,
    androidAudience: audience,
    suppressed,
  });
  if (destination === "android-landing") {
    return (
      <Suspense fallback={loading}>
        <AndroidLanding />
      </Suspense>
    );
  }
  return <Navigate to={destination} replace />;
}
